import { Button, Modal, notification } from 'antd'
import React, { useMemo, useRef, useState } from 'react'
import { ColDef, GridOptions } from 'ag-grid-community'
import {
  useCreateMissingEmployeesInAuthServiceMutation,
  useScanMissingEmployeesInAuthServiceMutation,
} from '@/features/user/user.api.ts'
import { MissingEmployeeInAuthService } from '@/features/user/user.def.ts'
import { handleApiError } from '@/utils/api.tsx'
import { CustomAgGrid, CustomAgGridRef } from '@/common/components/CustomAgGrid.tsx'

const missingEmployeeColumnDefs: ColDef<MissingEmployeeInAuthService>[] = [
  { colId: 'username', field: 'username', headerName: 'Username', filter: true },
  { colId: 'email', field: 'email', headerName: 'Email', filter: true },
  { colId: 'nickname', field: 'nickname', headerName: 'Nickname', filter: true },
  { colId: 'status', field: 'status', headerName: 'Status', filter: true },
  { colId: 'department', field: 'department', headerName: 'Department', filter: true },
  { colId: 'region', field: 'region', headerName: 'Region', filter: true },
  { colId: 'joinDate', field: 'joinDate', headerName: 'Join Date', filter: true },
]

export const MissingEmployeesInAuthServiceModal: React.FC = () => {
  const [open, setOpen] = useState(false)
  const [employees, setEmployees] = useState<MissingEmployeeInAuthService[]>([])
  const gridRef = useRef<CustomAgGridRef<MissingEmployeeInAuthService>>(null)
  const [scanMissingEmployeesInAuthService, { isLoading: isScanningMissingEmployees }] = useScanMissingEmployeesInAuthServiceMutation()
  const [createMissingEmployeesInAuthService, { isLoading: isCreatingMissingEmployees }] = useCreateMissingEmployeesInAuthServiceMutation()

  const handleCancel = () => {
    setOpen(false)
  }

  const handleScanMissingEmployeesInAuthService = async () => {
    try {
      const result = await scanMissingEmployeesInAuthService().unwrap()
      if (result.length === 0) {
        notification.success({ message: 'No missing employee found in Auth Service' })
        return
      }
      setEmployees(result)
      setOpen(true)
    } catch (e) {
      handleApiError(e, 'Failed to scan missing employees in Auth Service')
    }
  }

  const handleCreateMissingEmployeesInAuthService = async () => {
    const selectedEmployees = gridRef.current?.getSelectedRows() || []

    if (!selectedEmployees.length) {
      notification.error({ message: 'Please select at least one employee to create' })
      return
    }

    try {
      await createMissingEmployeesInAuthService({ employees: selectedEmployees }).unwrap()
      notification.success({ message: 'Missing employees created successfully' })
      setOpen(false)
      setEmployees([])
    } catch (e) {
      handleApiError(e, 'Failed to create missing employees in Auth Service')
    }
  }

  const gridOptions = useMemo<GridOptions<MissingEmployeeInAuthService>>(() => ({
    getRowId: params => params.data.username,
    pagination: undefined,
  }), [])

  return (
    <>
      <Button
        className="w-80"
        type="primary"
        loading={isScanningMissingEmployees}
        onClick={handleScanMissingEmployeesInAuthService}
      >
        Scan Missing Employees in Auth Service
      </Button>
      <Modal
        title="Missing Employees in Auth Service"
        open={open}
        width={1200}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button
            key="create"
            type="primary"
            loading={isCreatingMissingEmployees}
            onClick={handleCreateMissingEmployeesInAuthService}
          >
            Create Selected
          </Button>,
        ]}
      >
        <CustomAgGrid<MissingEmployeeInAuthService>
          ref={gridRef}
          height={500}
          rowData={employees}
          columnDefs={missingEmployeeColumnDefs}
          gridOptions={gridOptions}
        />
      </Modal>
    </>
  )
}
